(() => {
  const config = window.TwoFourSix || {};
  const drawer = document.querySelector('[data-cart-drawer]');
  const itemsEl = document.querySelector('[data-cart-items]');
  const emptyEl = document.querySelector('[data-cart-empty]');
  const footerEl = document.querySelector('[data-cart-footer]');
  const subtotalEl = document.querySelector('[data-cart-subtotal]');
  const loadingEl = document.querySelector('[data-cart-loading]');
  const errorEl = document.querySelector('[data-cart-error]');
  let cart = null;
  let busy = false;

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const formatMoney = (cents, currency = cart?.currency || 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency
      }).format(Number(cents || 0) / 100);
    } catch {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const setBusy = (next) => {
    busy = next;
    if (loadingEl) loadingEl.hidden = !next;
    document.querySelectorAll('[data-add-to-cart], [data-cart-line]').forEach((el) => {
      if ('disabled' in el) el.disabled = next || el.dataset.unavailable === 'true';
    });
  };

  const showError = (message = '') => {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = !message;
    if (message) window.setTimeout(() => { errorEl.hidden = true; }, 5000);
  };

  const openCart = () => {
    if (!drawer) return;
    document.body.classList.add('cart-drawer-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('[data-cart-open], #bag-pill').forEach((el) => el.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => drawer.focus({ preventScroll: true }));
  };

  const closeCart = () => {
    document.body.classList.remove('cart-drawer-open');
    drawer?.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-cart-open], #bag-pill').forEach((el) => el.setAttribute('aria-expanded', 'false'));
  };

  const render = (nextCart) => {
    cart = nextCart;
    document.querySelectorAll('[data-cart-count], #cart-count').forEach((el) => { el.textContent = String(cart.item_count); });
    if (!itemsEl || !emptyEl || !footerEl || !subtotalEl) return;
    emptyEl.hidden = cart.item_count > 0;
    footerEl.hidden = cart.item_count === 0;
    subtotalEl.textContent = formatMoney(cart.total_price, cart.currency);
    itemsEl.innerHTML = cart.items.map((item) => {
      const image = item.image ? `<img class="cart-item__thumb" src="${escapeHtml(item.image)}&width=240" alt="" loading="lazy">` : '<div class="cart-item__thumb"></div>';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      return `<article class="cart-item">
        ${image}
        <div class="cart-item__meta">
          <div class="cart-item__top">
            <div><a class="cart-item__name" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a><div class="cart-item__variant">${escapeHtml(variant)}</div></div>
            <div class="cart-item__price">${formatMoney(item.final_line_price, cart.currency)}</div>
          </div>
          <div class="cart-item__controls">
            <div class="cart-item__qty" aria-label="${escapeHtml(item.product_title)} quantity">
              <button type="button" data-cart-line="${escapeHtml(item.key)}" data-cart-quantity="${item.quantity - 1}" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-line="${escapeHtml(item.key)}" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
            <button class="cart-item__remove" type="button" data-cart-line="${escapeHtml(item.key)}" data-cart-quantity="0">Remove</button>
          </div>
        </div>
      </article>`;
    }).join('');
  };

  const request = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...options
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Shopify could not update the cart.');
    return data;
  };

  const refresh = async () => render(await request(`${config.root || '/'}cart.js`));

  const add = async (variantId, quantity = 1) => {
    if (busy || !variantId) return;
    setBusy(true);
    showError();
    try {
      await request(config.cartAdd || '/cart/add.js', {
        method: 'POST', body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Math.max(1, Number(quantity) || 1) }] })
      });
      await refresh();
      openCart();
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const change = async (key, quantity) => {
    if (busy) return;
    setBusy(true);
    showError();
    try {
      render(await request(config.cartChange || '/cart/change.js', {
        method: 'POST', body: JSON.stringify({ id: key, quantity: Number(quantity) })
      }));
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-close]')) { closeCart(); return; }
    if (event.target.closest('[data-cart-open], #bag-pill')) { openCart(); return; }
    const line = event.target.closest('[data-cart-line]');
    if (line) { change(line.dataset.cartLine, line.dataset.cartQuantity); return; }
    const addButton = event.target.closest('[data-add-to-cart]');
    if (addButton) {
      event.preventDefault();
      if (addButton.disabled || addButton.dataset.unavailable === 'true') return;
      const form = addButton.closest('form');
      const variantId = addButton.dataset.variantId || form?.querySelector('[name="id"]')?.value;
      const quantity = addButton.dataset.quantity || form?.querySelector('[name="quantity"]')?.value || 1;
      add(variantId, quantity);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    add(form.querySelector('[name="id"]')?.value, form.querySelector('[name="quantity"]')?.value || 1);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeCart();
    if ((event.key === 'Enter' || event.key === ' ') && event.target.closest('#bag-pill')) {
      event.preventDefault(); openCart();
    }
  });

  document.querySelectorAll('#bag-pill').forEach((el) => {
    el.setAttribute('role', 'button'); el.setAttribute('tabindex', '0'); el.setAttribute('aria-haspopup', 'dialog');
  });
  refresh().catch(() => {});
  window.TwoFourSixCart = { add, refresh, open: openCart, close: closeCart };
})();
